import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { Link } from 'react-router-dom';

export default function Home() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://cssspreadsheet.lifestyle';

  const webSiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "CSSSpreadsheet.lifestyle",
    "url": siteUrl,
    "description": "Discover the best CSSBuy spreadsheet lists, Kakobuy spreadsheets, Superbuy spreadsheets, and OOPBuy spreadsheets. Compare china shopping agents, track fashion items, and save up to 60% on sourcing costs with our curated spreadsheet directory.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": siteUrl + "/?s={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webSiteSchema) }}
      />

      <Navbar />

      <main>
        {/* ===== HERO SECTION ===== */}
        <section className="relative w-full min-h-[580px] md:min-h-[680px] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://readdy.ai/api/search-image?query=Moody%20urban%20streetwear%20editorial%20scene%20with%20sneakers%20hanging%20from%20a%20wire%20fence%20against%20a%20dusky%20city%20skyline%20silhouette%2C%20warm%20amber%20street%20lights%20reflecting%20on%20wet%20asphalt%2C%20cinematic%20atmosphere%20with%20fog%20and%20neon%20sign%20glow%20in%20distance%2C%20dark%20navy%20and%20burnt%20orange%20color%20palette%2C%20dramatic%20contemporary%20fashion%20photography%2C%20no%20people%20visible%2C%20gritty%20urban%20aesthetic&width=1800&height=900&seq=hero-bg-v2-main&orientation=landscape"
              alt="CSSBuy Spreadsheet fashion collection"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/45"></div>
          </div>

          <div className="relative z-10 w-full px-6 md:px-10 lg:px-16">
            <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-20">
              <div className="lg:w-[58%] animate-fade-in-up">
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-heading text-background-50 leading-[1.05] tracking-tight">
                  CSSBuy
                  <br />
                  Spreadsheet
                  <br />
                  <span className="text-primary-300">Hub</span>
                </h1>
              </div>

              <div className="lg:w-[38%] mt-2 lg:mt-6 animate-fade-in-up animate-delay-200">
                <p className="text-base md:text-lg text-background-50/85 leading-relaxed mb-8 max-w-md">
                  Discover, compare, and track the best CSSBuy spreadsheet lists, Kakobuy spreadsheets, Superbuy spreadsheets, and OOPBuy spreadsheets — all in one SEO-optimized hub.
                </p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-8 py-3.5 border-2 border-background-50/60 text-background-50 text-sm font-semibold rounded-full hover:bg-background-50 hover:text-foreground-900 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Buy It Now
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ===== TRUST BADGES ===== */}
        <section className="w-full bg-background-50 border-b border-background-200/60">
          <div className="px-6 md:px-10 py-6 flex flex-wrap items-center justify-center gap-6 md:gap-12">
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <i className="ri-checkbox-circle-fill text-primary-500 text-lg"></i>
              <span>1000+ Curated Products</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <i className="ri-checkbox-circle-fill text-primary-500 text-lg"></i>
              <span>Updated Daily</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <i className="ri-checkbox-circle-fill text-primary-500 text-lg"></i>
              <span>Trusted Agent Data</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <i className="ri-checkbox-circle-fill text-primary-500 text-lg"></i>
              <span>Beginner Friendly</span>
            </div>
          </div>
        </section>

        {/* ===== CATEGORY GRID ===== */}
        <section className="w-full bg-background-50 py-16 md:py-20">
          <div className="px-6 md:px-10">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 gap-3">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">
                  Shop by Category
                </h2>
                <p className="text-sm text-foreground-500 mt-1">
                  14 Curated Fashion Categories
                </p>
              </div>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                View All Categories <i className="ri-arrow-right-line ml-1"></i>
              </a>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {categories.map((cat, idx) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group relative rounded-xl overflow-hidden aspect-[4/5] cursor-pointer animate-fade-in-up"
                  style={{ animationDelay: `${idx * 0.05}s` }}
                  data-product-shop
                >
                  <img
                    src={cat.imageUrl}
                    alt={`${cat.name} category`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                    title={`${cat.name} - CSSBuy Spreadsheet category`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent"></div>

                  {cat.items && (
                    <span className="absolute top-3 right-3 px-2.5 py-1 bg-background-50/90 backdrop-blur-sm text-foreground-800 text-xs font-medium rounded-full">
                      {cat.items} Items
                    </span>
                  )}

                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-base md:text-lg font-semibold text-background-50 leading-tight">
                      {cat.name}
                    </h3>
                    <p className="text-xs md:text-sm text-background-50/70 mt-1">
                      {cat.subtitle}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* ===== VALUE PROPOSITION ===== */}
        <section className="w-full bg-background-100 py-16 md:py-20">
          <div className="px-6 md:px-10">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4">
                Why Use a CSSBuy Spreadsheet?
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                Save money, track items, and shop smarter with curated spreadsheet directories designed for Western fashion buyers.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="bg-background-50 rounded-xl p-6 text-center animate-fade-in-up">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-money-dollar-circle-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">Save Up to 60%</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  Cut sourcing costs dramatically by comparing agent prices through organized spreadsheet lists.
                </p>
              </div>

              <div className="bg-background-50 rounded-xl p-6 text-center animate-fade-in-up animate-delay-100">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-file-list-3-line text-2xl text-accent-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">Track Items Easily</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  Organize products from multiple China agents in one structured, searchable spreadsheet hub.
                </p>
              </div>

              <div className="bg-background-50 rounded-xl p-6 text-center animate-fade-in-up animate-delay-200">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-scales-3-line text-2xl text-secondary-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">Compare Agents</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  Side-by-side CSSBuy, Kakobuy, Superbuy, and OOPBuy comparisons to find your cheapest option.
                </p>
              </div>

              <div className="bg-background-50 rounded-xl p-6 text-center animate-fade-in-up animate-delay-300">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-flashlight-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">Find Trending Items</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  Discover hidden deals and community-vetted fashion finds updated daily on our spreadsheet directory.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ===== AGENT COMPARISON TABLE ===== */}
        <section className="w-full bg-background-50 py-16 md:py-20">
          <div className="px-6 md:px-10">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 text-center mb-10">
              Shopping Agent Comparison
            </h2>

            <div className="max-w-4xl mx-auto overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200">
                    <th className="text-left py-4 px-4 text-foreground-500 font-medium text-xs uppercase tracking-wider">Agent</th>
                    <th className="text-left py-4 px-4 text-foreground-500 font-medium text-xs uppercase tracking-wider">Pricing</th>
                    <th className="text-left py-4 px-4 text-foreground-500 font-medium text-xs uppercase tracking-wider">Speed</th>
                    <th className="text-left py-4 px-4 text-foreground-500 font-medium text-xs uppercase tracking-wider">Ease of Use</th>
                    <th className="text-left py-4 px-4 text-foreground-500 font-medium text-xs uppercase tracking-wider">Spreadsheet Quality</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-background-100 hover:bg-background-100/50 transition-colors">
                    <td className="py-4 px-4 font-semibold text-foreground-900">
                      <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="hover:text-primary-600 transition-colors whitespace-nowrap">CSSBuy</a>
                    </td>
                    <td className="py-4 px-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">Low</span>
                    </td>
                    <td className="py-4 px-4 text-foreground-700">Medium</td>
                    <td className="py-4 px-4 text-foreground-700">Medium</td>
                    <td className="py-4 px-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700">High</span>
                    </td>
                  </tr>
                  <tr className="border-b border-background-100 hover:bg-background-100/50 transition-colors">
                    <td className="py-4 px-4 font-semibold text-foreground-900">
                      <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="hover:text-primary-600 transition-colors whitespace-nowrap">Kakobuy</a>
                    </td>
                    <td className="py-4 px-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">Low</span>
                    </td>
                    <td className="py-4 px-4 text-foreground-700">Fast</td>
                    <td className="py-4 px-4 text-foreground-700">Easy</td>
                    <td className="py-4 px-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">Medium</span>
                    </td>
                  </tr>
                  <tr className="border-b border-background-100 hover:bg-background-100/50 transition-colors">
                    <td className="py-4 px-4 font-semibold text-foreground-900">
                      <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="hover:text-primary-600 transition-colors whitespace-nowrap">Superbuy</a>
                    </td>
                    <td className="py-4 px-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">Medium</span>
                    </td>
                    <td className="py-4 px-4 text-foreground-700">Fast</td>
                    <td className="py-4 px-4 text-foreground-700">Very Easy</td>
                    <td className="py-4 px-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700">High</span>
                    </td>
                  </tr>
                  <tr className="hover:bg-background-100/50 transition-colors">
                    <td className="py-4 px-4 font-semibold text-foreground-900">
                      <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="hover:text-primary-600 transition-colors whitespace-nowrap">OOPBuy</a>
                    </td>
                    <td className="py-4 px-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">Low</span>
                    </td>
                    <td className="py-4 px-4 text-foreground-700">Medium</td>
                    <td className="py-4 px-4 text-foreground-700">Medium</td>
                    <td className="py-4 px-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">Medium</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ===== SEO ARTICLE PREVIEW ===== */}
        <section className="w-full bg-background-100 py-16 md:py-20">
          <div className="px-6 md:px-10">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              <div className="lg:w-[42%] animate-fade-in-up">
                <span className="inline-block px-3 py-1 text-xs font-medium text-accent-700 bg-accent-100 rounded-full mb-4">
                  Latest SEO Guide
                </span>
                <h2 className="text-3xl md:text-4xl font-heading text-foreground-900 leading-tight mb-6">
                  What is CSSBuy
                  <br />
                  <span className="text-accent-500">Spreadsheet?</span>
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                  A CSSBuy spreadsheet is a curated digital catalog that organizes thousands of replica and affordable fashion items accessible through the CSSBuy shopping agent. Unlike browsing chaotic marketplaces, these spreadsheets filter products by category — sneakers, hoodies, accessories — allowing Western buyers to compare prices, view QC photos, and track links in one structured hub. Using a CSSBuy spreadsheet saves up to 60% on sourcing costs because every row links directly to Taobao or Weidian listings vetted by community editors.
                </p>
                <Link
                  to="/what-is-cssbuy-spreadsheet"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors border-b-2 border-foreground-900 hover:border-primary-600 pb-0.5 whitespace-nowrap"
                >
                  Read Full SEO Article
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="lg:w-[55%] flex gap-4 animate-fade-in-up animate-delay-200">
                <div className="flex-1 flex flex-col gap-4">
                  <div className="relative rounded-xl overflow-hidden aspect-square">
                    <img
                      src="https://readdy.ai/api/search-image?query=Top%20down%20flat%20lay%20of%20colorful%20sneakers%20scarves%20and%20fashion%20accessories%20arranged%20on%20a%20polished%20rose%20gold%20metallic%20surface%2C%20bright%20studio%20lighting%20with%20crisp%20clean%20reflections%2C%20vibrant%20editorial%20product%20photography%2C%20mix%20of%20coral%20pink%20mint%20green%20and%20soft%20yellow%20tones%2C%20playful%20modern%20composition%20with%20geometric%20arrangement&width=600&height=600&seq=seo-preview-v2-sq&orientation=squarish"
                      alt="CSSBuy spreadsheet products"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <p className="text-3xl md:text-4xl font-heading text-foreground-900">1,000+</p>
                      <p className="text-xs text-foreground-500 mt-1">Curated Items</p>
                    </div>
                    <div className="flex-1">
                      <p className="text-3xl md:text-4xl font-heading text-foreground-900">Daily</p>
                      <p className="text-xs text-foreground-500 mt-1">Updated Lists</p>
                    </div>
                  </div>
                </div>
                <div className="flex-1 relative rounded-xl overflow-hidden aspect-[3/4] mt-8">
                  <img
                    src="https://readdy.ai/api/search-image?query=Stylish%20denim%20jacket%20with%20embroidered%20patches%20hanging%20on%20a%20vintage%20mint%20green%20painted%20wooden%20chair%20in%20a%20sunlit%20room%20with%20houseplants%2C%20bohemian%20eclectic%20interior%20aesthetic%2C%20warm%20golden%20afternoon%20sunlight%20streaming%20through%20sheer%20curtains%2C%20lifestyle%20fashion%20photography%2C%20mix%20of%20indigo%20blue%20against%20soft%20sage%20green%20and%20warm%20amber%20tones&width=600&height=800&seq=seo-preview-v2-portrait&orientation=portrait"
                    alt="CSSBuy spreadsheet fashion"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== FULL-SCREEN CTA ===== */}
        <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Energetic%20lifestyle%20scene%20with%20fashion%20shopping%20bags%20and%20sneakers%20on%20a%20vibrant%20coral%20pink%20backdrop%20with%20tropical%20palm%20leaf%20shadows%2C%20bright%20sunny%20daylight%20casting%20playful%20shadow%20patterns%2C%20bold%20summer%20fashion%20editorial%20photography%2C%20vivid%20coral%20pink%20lime%20green%20and%20sunshine%20yellow%20color%20palette%2C%20cheerful%20and%20aspirational%20shopping%20aesthetic&width=1800&height=800&seq=cta-bg-v2-full&orientation=landscape"
            alt="Start shopping smarter with CSSBuy Spreadsheet"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/35 via-black/20 to-black/40"></div>

          <div className="absolute inset-0 flex items-center justify-start px-8 md:px-16 lg:px-20">
            <div className="max-w-xl">
              <h2 className="text-4xl md:text-6xl font-heading text-background-50 leading-tight mb-6">
                start shopping smarter
              </h2>
              <p className="text-base md:text-lg text-background-50/80 leading-relaxed mb-8 max-w-md">
                Explore the best CSSBuy spreadsheet collections now and save up to 60% on your next fashion haul.
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-background-50 text-foreground-900 text-sm font-semibold rounded-full hover:bg-background-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Explore All
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-foreground-900">
                  <i className="ri-arrow-right-line text-background-50 text-xs"></i>
                </span>
              </a>
            </div>
          </div>
        </section>

        {/* ===== SEO KEYWORD SECTION WITH IMAGES ===== */}
        <section className="w-full bg-background-50 py-16 md:py-20">
          <div className="px-6 md:px-10">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 text-center mb-4">
              Explore More Spreadsheet Guides
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10 max-w-xl mx-auto">
              Dive deeper into CSSBuy spreadsheets, agent comparisons, and shopping optimization tips.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5 max-w-5xl mx-auto">
              <Link
                to="/what-is-cssbuy-spreadsheet"
                className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-200/80 transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Clean%20modern%20desk%20setup%20with%20a%20laptop%20displaying%20colorful%20spreadsheet%20data%20and%20fashion%20product%20thumbnails%2C%20warm%20desk%20lamp%20glow%2C%20organized%20workspace%20aesthetic%2C%20soft%20natural%20window%20light%2C%20productive%20lifestyle%20photography%2C%20neutral%20grey%20and%20warm%20wood%20tones&width=400&height=300&seq=kw-cssbuy-guide&orientation=landscape"
                    alt="CSSBuy Spreadsheet Guide"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="text-sm font-medium text-foreground-800 group-hover:text-foreground-950 transition-colors">
                    CSSBuy Spreadsheet Guide
                  </p>
                </div>
              </Link>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-200/80 transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Minimalist%20product%20photography%20of%20Japanese%20inspired%20stationery%20and%20a%20sleek%20tablet%20on%20a%20light%20oak%20desk%20surface%2C%20zen%20garden%20sand%20pattern%20in%20background%2C%20calm%20and%20focused%20aesthetic%2C%20soft%20diffused%20daylight%2C%20harmonious%20beige%20and%20sage%20green%20tones&width=400&height=300&seq=kw-kakobuy&orientation=landscape"
                    alt="Kakobuy Spreadsheet"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="text-sm font-medium text-foreground-800 group-hover:text-foreground-950 transition-colors">
                    Kakobuy Spreadsheet
                  </p>
                </div>
              </a>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-200/80 transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20smartphone%20displaying%20a%20sleek%20shopping%20app%20interface%20on%20a%20marble%20cafe%20table%20with%20a%20cup%20of%20latte%20art%20coffee%20beside%20it%2C%20cozy%20coffee%20shop%20ambiance%20with%20warm%20pendant%20lighting%2C%20lifestyle%20tech%20photography%2C%20rich%20brown%20espresso%20and%20cream%20tones&width=400&height=300&seq=kw-superbuy&orientation=landscape"
                    alt="Superbuy Spreadsheet"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="text-sm font-medium text-foreground-800 group-hover:text-foreground-950 transition-colors">
                    Superbuy Spreadsheet
                  </p>
                </div>
              </a>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-200/80 transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Fashion%20moodboard%20collage%20pinned%20on%20a%20cork%20board%20wall%20with%20fabric%20swatches%20polaroid%20photos%20and%20handwritten%20notes%2C%20creative%20studio%20workspace%20with%20natural%20light%20from%20a%20large%20window%2C%20artistic%20lifestyle%20photography%2C%20warm%20terracotta%20mustard%20yellow%20and%20cream%20color%20palette&width=400&height=300&seq=kw-oopbuy&orientation=landscape"
                    alt="OOPBuy Spreadsheet"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="text-sm font-medium text-foreground-800 group-hover:text-foreground-950 transition-colors">
                    OOPBuy Spreadsheet
                  </p>
                </div>
              </a>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-200/80 transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=World%20map%20with%20push%20pins%20and%20a%20magnifying%20glass%20on%20a%20rustic%20wooden%20desk%2C%20travel%20planning%20aesthetic%2C%20warm%20vintage%20lighting%2C%20explorer%20lifestyle%20photography%2C%20rich%20brown%20leather%20and%20aged%20paper%20tones%2C%20globetrotter%20atmosphere&width=400&height=300&seq=kw-china-agent&orientation=landscape"
                    alt="China Agent Guide"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="text-sm font-medium text-foreground-800 group-hover:text-foreground-950 transition-colors">
                    China Agent Guide
                  </p>
                </div>
              </a>

              <Link
                to="/kakobuy-spreadsheet-guide"
                className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-200/80 transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Open%20notebook%20with%20handwritten%20shopping%20tips%20and%20a%20steaming%20cup%20of%20herbal%20tea%20on%20a%20cozy%20knitted%20blanket%2C%20soft%20morning%20light%20from%20a%20nearby%20window%2C%20hygge%20lifestyle%20photography%2C%20warm%20oatmeal%20cream%20and%20soft%20grey%20tones%2C%20comforting%20and%20inviting%20atmosphere&width=400&height=300&seq=kw-beginner&orientation=landscape"
                    alt="Beginner Shopping Guide"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="text-sm font-medium text-foreground-800 group-hover:text-foreground-950 transition-colors">
                    Beginner Shopping Guide
                  </p>
                </div>
              </Link>

              <Link
                to="/agent-comparison-2026"
                className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-200/80 transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Cardboard%20shipping%20boxes%20with%20packing%20tape%20and%20a%20digital%20scale%20on%20a%20clean%20industrial%20concrete%20floor%2C%20ecommerce%20fulfillment%20aesthetic%2C%20crisp%20overhead%20lighting%2C%20professional%20logistics%20photography%2C%20brown%20kraft%20paper%20and%20cool%20grey%20tones%2C%20organized%20warehouse%20vibe&width=400&height=300&seq=kw-shipping&orientation=landscape"
                    alt="Shipping Cost Guide"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="text-sm font-medium text-foreground-800 group-hover:text-foreground-950 transition-colors">
                    Shipping &amp; Cost Guide
                  </p>
                </div>
              </Link>

              <Link
                to="/what-is-cssbuy-spreadsheet"
                className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-200/80 transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Step%20by%20step%20instruction%20cards%20laid%20out%20on%20a%20light%20wood%20desk%20with%20colorful%20sticky%20notes%20and%20highlighters%2C%20organized%20learning%20aesthetic%2C%20bright%20natural%20daylight%2C%20educational%20lifestyle%20photography%2C%20clean%20white%20pastel%20yellow%20and%20soft%20blue%20tones&width=400&height=300&seq=kw-tutorial&orientation=landscape"
                    alt="Beginner Tutorial"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="text-sm font-medium text-foreground-800 group-hover:text-foreground-950 transition-colors">
                    Beginner Tutorial
                  </p>
                </div>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}